/**
 * product-faq.js
 * FAQ produit : bascule d'onglets, recherche, tracking.
 *
 * Contrat de markup consommé : docs/plans/2026-07-30-faq-produit/state/contrat-markup.md
 * - `data-faq-root` : racine, `data-faq-tab` : boutons d'onglet, `data-faq-cat` : items
 * - `data-faq-search` : haystack (question + réponse, minuscules sans accents)
 * - `data-faq-search-input`, `data-faq-count`, `data-faq-empty`, `data-faq-cta`
 *
 * 🔴 AUCUNE manipulation du DOM au chargement : l'état initial (onglet « Ce vélo » actif,
 * seul rendu côté serveur) est déjà correct — toute écriture ici casserait le CLS.
 * L'index de recherche est construit PARESSEUSEMENT, à la première frappe (~8 ms).
 *
 * 🔴 HYDRATATION : seule la catégorie « Ce vélo » est rendue par le serveur. Les 5 autres
 * arrivent en données inertes dans `[data-faq-payload]` et ne deviennent du DOM qu'à la
 * PREMIÈRE interaction (clic d'onglet, focus ou frappe dans la recherche). C'est ce qui les
 * garde hors index : Googlebot rend le JS mais ne clique ni ne tape, donc son DOM rendu ne
 * les contient jamais — cf. l'en-tête de `snippets/product-faq.liquid` pour le pourquoi.
 * Corollaire : rien ne doit lire `items` / `headings` en supposant le corpus complet sans
 * avoir appelé `hydrate()` d'abord.
 *
 * Recherche : tokens + ET sur tous les termes (ordre libre) + racinisation légère FR +
 * synonymes métier + score titre > catégorie > réponse. Diagnostic qui a motivé ce moteur
 * (mesuré sur les 71 questions réelles) : un `indexOf` de la chaîne entière renvoyait 0
 * résultat dès que la requête faisait deux mots — « délai livraison » ne trouvait pas
 * « Quels sont les délais et les prix de livraison ? ».
 *
 * Modèle : product-footer-cta.js (IIFE + 'use strict' + helper pushEvent + délégation)
 * et le garde document.readyState de product-anchor-nav.js.
 */
(function () {
  'use strict';

  var FILTER_DEBOUNCE_MS = 200;
  var TRACK_DEBOUNCE_MS = 800;
  var TRACK_MIN_LENGTH = 3;

  // Racinisation : suffixes du plus long au plus court, racine conservée ≥ MIN_STEM.
  // Règle le « un s de trop = zéro résultat » (« batteries », « garanties ») et rapproche
  // les formes verbales des formes nominales du corpus (« réparer » ↔ « réparation »).
  var MIN_STEM = 4;
  var SUFFIXES = (
    'ations ation ements ement ances ance ences ence ables able ibles ible ' +
    'ites ite eries erie ages age eaux aux ees ent ons ez er ir es ee e s x'
  ).split(' ');

  // Synonymes métier RELEVÉS SUR LE CORPUS : chaque ligne = « mot du corpus » suivi des
  // mots que le client tape à la place. Uniquement ce que ni la racinisation ni le libellé
  // de catégorie ne rattrapent (mesuré sur les 71 questions). Volontairement court : chaque
  // alias élargit le rappel, une table bavarde ramène du bruit.
  var SYNONYM_GROUPS = [
    'prix tarif tarifs cout couts',
    'batterie autonomie recharge',
    'reparation depannage reparer',
    'panne casse',
    'retour annuler annulation retracter retractation retourner',
    'rembourse rembourser remboursement',
    'boutique magasin magasins revendeur velociste',
    'lld leasing louer abonnement mensualite duree',
    'alma 2x 3x 4x 10x fractionne klarna paypal',
    'bancaire cb visa mastercard',
    'joindre contact appeler',
    'telephone numero',
    'essai essayer tester',
    'assurance assurer',
    'vol voler',
    'monte montage',
    'transporteur colis',
    'suivi suivre',
    'reprise reprendre',
    'echange echanger',
  ];
  var SYNONYMS = {};
  SYNONYM_GROUPS.forEach(function (group) {
    var words = group.split(' ');
    for (var i = 1; i < words.length; i++) SYNONYMS[words[i]] = words[0];
  });

  // Mots vides : sans eux, « puis-je faire … » impose des termes que porte la moitié du
  // corpus et le ET ne discrimine plus rien.
  var STOPWORDS = {};
  (
    'le la les un une des du de et ou a au aux en est il je ce cet cette mon ma mes pour ' +
    'dans que qui quoi quel quelle sur par avec pas plus si y l d n s c j m t qu comment ' +
    'puis dois faire vous me se ne on sont ai'
  )
    .split(' ')
    .forEach(function (w) {
      STOPWORDS[w] = true;
    });

  function pushEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  function debugWarn() {
    // `window.DEBUG_MODE` n'existe pas — tracking-core.liquid déclare `const DEBUG_MODE`
    // en scope lexical de script, pas une propriété window (cf. product-gallery.js).
    if (typeof DEBUG_MODE !== 'undefined' && DEBUG_MODE) {
      console.warn.apply(console, ['[product-faq]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // Bloc `ecommerce` standard de la fiche produit, construit par `tracking-ecommerce.liquid`
  // (seul endroit où les variables Liquid `product.title`, `clean_variant_title`, la devise…
  // sont disponibles — un asset JS statique ne peut pas les lire). Sans lui, les events de
  // la FAQ ne se joignent pas au funnel GA4 et `option_fmd` manque, donc impossible de
  // comparer l'usage de la FAQ entre parcours standard et FMD.
  // Renvoie `undefined` hors fiche produit : la clé disparaît simplement du payload.
  function productEcommerce() {
    if (typeof window.mbProductEcommerce !== 'function') return undefined;
    try {
      return window.mbProductEcommerce();
    } catch (e) {
      debugWarn('mbProductEcommerce a échoué — event émis sans bloc ecommerce.');
      return undefined;
    }
  }

  // Normalisation d'accents locale (aucune fonction globale réutilisable dans le thème) :
  // pattern buyback-portal-detail-helpers.js (toLowerCase + NFD + strip des combinantes).
  function normalize(str) {
    var s = String(str || '').toLowerCase();
    try {
      s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    } catch (e) {}
    return s;
  }

  function stem(word) {
    for (var i = 0; i < SUFFIXES.length; i++) {
      var suf = SUFFIXES[i];
      if (word.length - suf.length >= MIN_STEM && word.slice(-suf.length) === suf) {
        return word.slice(0, word.length - suf.length);
      }
    }
    return word;
  }

  function tokenize(str) {
    var raw = normalize(str)
      .replace(/['’]/g, ' ')
      .split(/[^a-z0-9]+/);
    var seen = {};
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var w = raw[i];
      if (!w || STOPWORDS[w] || seen[w]) continue;
      seen[w] = true;
      out.push({ w: w, s: stem(w) });
    }
    return out;
  }

  // Trois voies, du plus strict au plus tolérant :
  //  1. mot ou racine identiques ;
  //  2. une racine préfixe l'autre — rattrape les racinisations asymétriques
  //     (« reprise » → repris, « repris » → repri) ;
  //  3. le mot tapé préfixe le mot indexé — recherche pendant la frappe (« bat » →
  //     « batterie »), et le PRÉFIXE seul, jamais la sous-chaîne : « vol » atteint
  //     « volé » sans ramener « évolution » ni « entreprise » pour « reprise ».
  function matches(term, token) {
    if (term.w === token.w || term.s === token.s) return true;
    if (term.s.length >= MIN_STEM && token.s.indexOf(term.s) === 0) return true;
    if (token.s.length >= MIN_STEM && term.s.indexOf(token.s) === 0) return true;
    return token.w.indexOf(term.w) === 0;
  }

  // 2 pour une correspondance directe, 1 via synonyme : à score de champ égal, le mot
  // que l'utilisateur a réellement tapé passe devant son synonyme.
  function fieldHit(field, variants) {
    for (var v = 0; v < variants.length; v++) {
      for (var i = 0; i < field.length; i++) {
        if (matches(variants[v], field[i])) return v === 0 ? 2 : 1;
      }
    }
    return 0;
  }

  function expand(term) {
    var variants = [term];
    var syn = SYNONYMS[term.w];
    if (syn) {
      syn.split(' ').forEach(function (t) {
        variants.push({ w: t, s: stem(t) });
      });
    }
    return variants;
  }

  function init() {
    var root = document.querySelector('[data-faq-root]');
    if (!root) return;

    var list = root.querySelector('.product-faq__list');
    var tabs = root.querySelectorAll('[data-faq-tab]');
    // `items` et `headings` sont RÉASSIGNÉS par refreshRefs() après hydratation :
    // querySelectorAll rend une liste STATIQUE, les nœuds injectés n'y entreraient jamais.
    var items = root.querySelectorAll('.product-faq__item[data-faq-cat]');
    // Intertitres de regroupement par catégorie affichés en mode recherche (toutes
    // catégories confondues) — no-op si le snippet n'en rend aucun.
    var headings = root.querySelectorAll('.product-faq__heading[data-faq-cat]');
    var payloadEl = root.querySelector('[data-faq-payload]');
    var searchInput = root.querySelector('[data-faq-search-input]');
    var clearBtn = root.querySelector('[data-faq-search-clear]');
    var countEl = root.querySelector('[data-faq-count]');
    var emptyEl = root.querySelector('[data-faq-empty]');
    var countTemplate = countEl ? countEl.getAttribute('data-faq-count-template') : '';

    if (countEl && !countTemplate) {
      debugWarn('data-faq-count-template absent sur [data-faq-count] — compteur en repli brut.');
    }

    var activeTab = getActiveTabKey();
    var index = null;
    var domOrder = null;
    var isReordered = false;
    var hydrated = !payloadEl;
    var lastSearch = { count: 0, mode: 'none' };

    function getActiveTabKey() {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].classList.contains('is-active')) {
          return tabs[i].getAttribute('data-faq-tab');
        }
      }
      return tabs.length ? tabs[0].getAttribute('data-faq-tab') : '';
    }

    function refreshRefs() {
      items = root.querySelectorAll('.product-faq__item[data-faq-cat]');
      headings = root.querySelectorAll('.product-faq__heading[data-faq-cat]');
    }

    function tabLabel(key) {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('data-faq-tab') === key) return tabs[i].textContent.trim();
      }
      return '';
    }

    // Construit les catégories génériques depuis `[data-faq-payload]`. Idempotent, appelé
    // par TOUS les points d'entrée utilisateur — jamais au chargement (sauf filet
    // `data-faq-hydrate-now`, cf. plus bas).
    //
    // Les items naissent `hidden` et sont ajoutés APRÈS ceux du serveur, dans l'ordre du
    // payload : l'ordre du DOM reste celui du metaobject, et rien ne bouge à l'écran (la
    // catégorie visible, elle, est déjà en place). Une seule écriture DOM, via fragment.
    //
    // `textContent` pour la question (jamais innerHTML : c'est du texte, et le serveur
    // l'`escape` de son côté), `innerHTML` pour la réponse, qui EST du HTML — rich text
    // rendu + CTA de composant. Frontière de confiance identique au rendu serveur : la
    // source est le metaobject `faq_produit`, saisi en admin, jamais une entrée visiteur,
    // et le HTML injecté est exactement celui que `{{ faq_answer }}` écrit pour `ce_velo`.
    // Le payload traverse `| json` + rééchappement `<` / `>` côté Liquid.
    function hydrate() {
      if (hydrated) return;
      hydrated = true;
      var data;
      try {
        data = JSON.parse(payloadEl.textContent);
      } catch (e) {
        debugWarn('payload FAQ illisible — catégories génériques absentes.', e);
        return;
      }
      if (!list || !data || !data.length) return;

      var frag = document.createDocumentFragment();
      var seenCat = {};
      for (var i = 0; i < data.length; i++) {
        var row = data[i];
        if (!row || !row.c) continue;
        if (!seenCat[row.c]) {
          seenCat[row.c] = true;
          // Libellé pris sur l'onglet : même chaîne localisée que celle dont le serveur
          // fait ses intertitres `ce_velo`, sans la dupliquer dans le payload.
          var heading = document.createElement('li');
          heading.className = 'product-faq__heading';
          heading.setAttribute('data-faq-cat', row.c);
          heading.hidden = true;
          heading.textContent = tabLabel(row.c);
          frag.appendChild(heading);
        }
        var li = document.createElement('li');
        li.className = 'product-faq__item';
        li.setAttribute('data-faq-cat', row.c);
        li.hidden = true;
        li.setAttribute('data-faq-search', row.s || '');
        var details = document.createElement('details');
        details.className = 'product-faq__details';
        var summary = document.createElement('summary');
        summary.className = 'product-faq__question';
        summary.textContent = row.q || '';
        var answer = document.createElement('div');
        answer.className = 'product-faq__answer';
        answer.innerHTML = row.a || '';
        details.appendChild(summary);
        details.appendChild(answer);
        li.appendChild(details);
        frag.appendChild(li);
      }
      list.appendChild(frag);
      refreshRefs();
      // L'index et l'ordre DOM mémorisés ne connaissent pas les nouveaux nœuds.
      index = null;
      domOrder = null;
      // Le payload est figé au rendu serveur, donc sur la variante chargée. Si la taille a
      // changé AVANT l'hydratation, les 5 valeurs `data-faq-var` des questions Paiement
      // (`[prix]`, `[acompte]`) naîtraient sur l'ancienne variante — d'où ce rattrapage.
      resyncVariantValues(lastVariant);
    }

    // Libellés d'onglet = seule source du nom de catégorie côté client (i18n incluse).
    // Les indexer fait qu'une recherche « SAV » ou « paiement » remonte toute la
    // catégorie : le libellé du metaobject n'apparaît dans presque aucune réponse.
    function categoryTokens() {
      var byKey = {};
      for (var i = 0; i < tabs.length; i++) {
        byKey[tabs[i].getAttribute('data-faq-tab')] = tokenize(tabs[i].textContent);
      }
      return byKey;
    }

    // Construit l'index à la première frappe seulement (~8 ms sur 71 questions).
    function ensureIndex() {
      if (index) return;
      var catTokens = categoryTokens();
      index = [];
      for (var i = 0; i < items.length; i++) {
        var el = items[i];
        var questionEl = el.querySelector('.product-faq__question');
        var cat = el.getAttribute('data-faq-cat');
        index.push({
          el: el,
          cat: cat,
          title: tokenize(questionEl ? questionEl.textContent : ''),
          catLabel: catTokens[cat] || [],
          // `data-faq-search` porte question + réponse : suffisant comme champ « corps »,
          // le titre étant déjà indexé à part avec un poids supérieur.
          body: tokenize(el.getAttribute('data-faq-search')),
          hits: 0,
          score: 0,
          shown: false,
        });
      }
      if (list) domOrder = Array.prototype.slice.call(list.children);
    }

    // Croix d'effacement : visible dès qu'il y a du texte. Le bouton est en position
    // absolue, hors flux — le révéler ne déplace donc rien (pas de CLS), ce qui autorise
    // l'appel au chargement quand le navigateur restaure une saisie (retour arrière).
    function syncClearButton() {
      if (!clearBtn || !searchInput) return;
      clearBtn.hidden = searchInput.value === '';
    }

    function updateCount(count) {
      if (!countEl) return;
      countEl.textContent = countTemplate ? countTemplate.replace('__COUNT__', count) : String(count);
      countEl.hidden = false;
    }

    // Une seule écriture DOM : les nœuds réordonnés partent dans un DocumentFragment,
    // jamais item par item (71 déplacements naïfs = reflows en cascade).
    function reflow(nodes) {
      if (!list || !nodes.length) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < nodes.length; i++) frag.appendChild(nodes[i]);
      list.appendChild(frag);
    }

    function restoreDomOrder() {
      if (!isReordered || !domOrder) return;
      reflow(domOrder.slice());
      isReordered = false;
    }

    // Vue par onglet : une seule catégorie visible, pas de compteur ni d'intertitres.
    function showTab(key) {
      activeTab = key;
      restoreDomOrder();
      for (var i = 0; i < tabs.length; i++) {
        var isActive = tabs[i].getAttribute('data-faq-tab') === key;
        tabs[i].classList.toggle('is-active', isActive);
        tabs[i].setAttribute('aria-pressed', String(isActive));
      }
      for (var j = 0; j < items.length; j++) {
        items[j].hidden = items[j].getAttribute('data-faq-cat') !== key;
      }
      for (var k = 0; k < headings.length; k++) {
        headings[k].hidden = true;
      }
      if (countEl) countEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
    }

    // ET sur tous les termes ; si aucune question ne les porte tous, on retombe sur les
    // meilleures partielles plutôt que sur une page vide.
    function score(terms) {
      var matched = [];
      var full = [];
      for (var i = 0; i < index.length; i++) {
        var doc = index[i];
        doc.hits = 0;
        doc.score = 0;
        doc.shown = false;
        for (var t = 0; t < terms.length; t++) {
          var variants = terms[t].variants;
          var hit = fieldHit(doc.title, variants) * 10;
          if (!hit) hit = fieldHit(doc.catLabel, variants) * 4;
          if (!hit) hit = fieldHit(doc.body, variants);
          if (hit) {
            doc.hits++;
            doc.score += hit;
          }
        }
        if (doc.hits === terms.length) full.push(doc);
        if (doc.hits) matched.push(doc);
      }
      var result = full.length ? { docs: full, mode: 'all' } : { docs: matched, mode: 'partial' };
      // `shown` et non `hits > 0` : en mode 'all', une question qui ne porte qu'une partie
      // des termes doit rester masquée, sinon l'affichage contredit le compteur.
      for (var d = 0; d < result.docs.length; d++) result.docs[d].shown = true;
      return result;
    }

    // Tri de pertinence qui PRÉSERVE le regroupement par catégorie des intertitres :
    // catégories ordonnées par leur meilleur score, questions ordonnées dans chacune.
    function orderedNodes(docs) {
      var groups = {};
      var order = [];
      for (var i = 0; i < docs.length; i++) {
        var key = docs[i].cat;
        if (!groups[key]) {
          groups[key] = { key: key, best: 0, docs: [] };
          order.push(groups[key]);
        }
        groups[key].docs.push(docs[i]);
        if (docs[i].score > groups[key].best) groups[key].best = docs[i].score;
      }
      order.sort(function (a, b) {
        return b.best - a.best;
      });
      var headingByCat = {};
      for (var h = 0; h < headings.length; h++) {
        headingByCat[headings[h].getAttribute('data-faq-cat')] = headings[h];
      }
      var nodes = [];
      for (var g = 0; g < order.length; g++) {
        order[g].docs.sort(function (a, b) {
          return b.score - a.score;
        });
        var heading = headingByCat[order[g].key];
        if (heading) nodes.push(heading);
        for (var d = 0; d < order[g].docs.length; d++) nodes.push(order[g].docs[d].el);
      }
      return nodes;
    }

    // Vue recherche : toutes catégories confondues, aucun onglet représentatif.
    function applyFilter(rawQuery) {
      // La recherche porte sur TOUTES les catégories : hydrater avant d'indexer, sinon elle
      // ne trouverait que les questions propres au vélo.
      hydrate();
      ensureIndex();
      var terms = tokenize(rawQuery).map(function (term) {
        return { variants: expand(term) };
      });
      if (!terms.length) {
        lastSearch = { count: 0, mode: 'none' };
        showTab(activeTab);
        return;
      }

      var result = score(terms);
      var visible = {};
      for (var i = 0; i < result.docs.length; i++) visible[result.docs[i].cat] = true;
      for (var j = 0; j < index.length; j++) index[j].el.hidden = index[j].shown === false;
      for (var h = 0; h < headings.length; h++) {
        headings[h].hidden = !visible[headings[h].getAttribute('data-faq-cat')];
      }
      for (var k = 0; k < tabs.length; k++) {
        tabs[k].classList.remove('is-active');
        tabs[k].setAttribute('aria-pressed', 'false');
      }

      if (result.docs.length) {
        reflow(orderedNodes(result.docs));
        isReordered = true;
      }
      lastSearch = { count: result.docs.length, mode: result.docs.length ? result.mode : 'none' };
      updateCount(result.docs.length);
      if (emptyEl) emptyEl.hidden = result.docs.length !== 0;
    }

    // --- Une seule délégation pour les clics (onglets + CTA de composant) ---
    root.addEventListener('click', function (e) {
      var tabBtn = e.target.closest('[data-faq-tab]');
      if (tabBtn) {
        var key = tabBtn.getAttribute('data-faq-tab');
        if (key === activeTab && (!searchInput || !searchInput.value)) return;
        // Avant showTab : sans hydratation, l'onglet cliqué n'a aucun item dans le DOM.
        hydrate();
        if (searchInput) searchInput.value = '';
        syncClearButton();
        showTab(key);
        pushEvent('click_cta', {
          cta_name: tabBtn.getAttribute('data-cta-name') || key,
          cta_location: 'product_faq',
          faq_action: 'category',
          ecommerce: productEcommerce(),
        });
        return;
      }

      var ctaBtn = e.target.closest('[data-faq-cta]');
      if (ctaBtn) {
        var item = ctaBtn.closest('.product-faq__item');
        var questionEl = item ? item.querySelector('.product-faq__question') : null;
        pushEvent('click_cta', {
          cta_name: ctaBtn.getAttribute('data-cta-name') || '',
          cta_location: root.getAttribute('data-cta-location') || 'product_faq',
          faq_action: 'cta',
          question_label: questionEl ? questionEl.textContent.trim() : '',
          ecommerce: productEcommerce(),
        });
        // Certains CTA ne sont pas des liens mais des PROXYS de clic vers un bouton
        // déjà présent sur la fiche (ex. [CTA_LLD] → .greenleaze-button-product).
        // Même patron que product-financing-cta.js:33-36 et lld-header-banner-price.js:59-64.
        // Sans ceci, le bouton s'affiche et ne fait rien — et comme il n'est rendu que
        // sur les fiches éligibles LLD, le défaut passerait facilement la recette.
        var proxySel = ctaBtn.getAttribute('data-faq-proxy');
        if (proxySel) {
          // `:not([disabled])` dans le sélecteur : `.click()` sur un contrôle désactivé ne
          // dispatche rien (spec HTML), le CTA serait mort en silence. Et on cherche parmi
          // TOUS les candidats — `.reserve-btn` est rendu par 3 chemins (sticky, footer,
          // formulaire) et `querySelector` seul aurait pris le premier du document, même
          // masqué ou désactivé.
          var trigger = document.querySelector(
            proxySel
              .split(',')
              .map(function (sel) {
                return sel.trim() + ':not([disabled])';
              })
              .join(','),
          );
          if (trigger) {
            e.preventDefault();
            trigger.click();
          } else {
            debugWarn('CTA proxy sans cible active :', proxySel);
          }
        }
      }
    });

    // --- `toggle` ne bubble pas → capture déléguée sur la racine (précédent :
    // tracking-ecommerce.liquid, bloc caractéristiques). Un <summary> n'est jamais
    // capté par le tracker générique de tracking-ui-forms.liquid : pas de doublon.
    root.addEventListener(
      'toggle',
      function (e) {
        var details = e.target;
        if (!details || !details.matches || !details.matches('.product-faq__item > details')) return;
        // Ouverture ET refermeture mesurées, comme l'accordéon caractéristiques
        // (tracking-ecommerce.liquid, `spec_action`) : savoir qu'une question est refermée
        // aussitôt est un signal de réponse ratée, autant que son ouverture.
        var item = details.closest('.product-faq__item');
        var summary = details.querySelector('.product-faq__question');
        pushEvent('faq_question_open', {
          question_label: summary ? summary.textContent.trim() : '',
          category: item ? item.getAttribute('data-faq-cat') : '',
          faq_action: details.open ? 'open' : 'close',
          ecommerce: productEcommerce(),
        });
      },
      true,
    );

    // --- Recherche : filtre (200 ms) + tracking (800 ms, ≥ 3 caractères) ---
    if (searchInput) {
      var filterTimer = null;
      var trackTimer = null;

      // Hydratation au focus : le travail passe AVANT la frappe plutôt que pendant, là où
      // il se verrait (la construction reste sur le thread principal). `applyFilter` la
      // rappelle de toute façon — c'est de l'anticipation, pas le garde-fou.
      searchInput.addEventListener('focus', hydrate);

      searchInput.addEventListener('input', function () {
        var raw = searchInput.value.trim();
        syncClearButton();

        clearTimeout(filterTimer);
        filterTimer = setTimeout(function () {
          applyFilter(raw);
        }, FILTER_DEBOUNCE_MS);

        clearTimeout(trackTimer);
        if (normalize(raw).length >= TRACK_MIN_LENGTH) {
          trackTimer = setTimeout(function () {
            // `mode` distingue les trois issues sans créer un 5e event : 'all' (toutes les
            // questions portent tous les termes), 'partial' (repli sur les plus proches),
            // 'none' (aucun résultat) — c'est la mesure qui alimentera SYNONYMS.
            pushEvent('faq_search', {
              search_term: raw,
              results_count: lastSearch.count,
              search_mode: lastSearch.mode,
              ecommerce: productEcommerce(),
            });
          }, TRACK_DEBOUNCE_MS);
        }
      });

      // Effacement : on annule les deux debounces en cours avant de rendre la vue par
      // onglet. Sans le clearTimeout du filtre, un applyFilter() programmé juste avant le
      // clic se déclencherait APRÈS et réafficherait les résultats d'une requête effacée.
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          clearTimeout(filterTimer);
          clearTimeout(trackTimer);
          searchInput.value = '';
          syncClearButton();
          applyFilter('');
          searchInput.focus();
          pushEvent('click_cta', {
            cta_name: 'faq_search_clear',
            cta_location: root.getAttribute('data-cta-location') || 'product_faq',
            faq_action: 'search_clear',
            ecommerce: productEcommerce(),
          });
        });
      }

      syncClearButton();
    }

    // Filet du cas dégénéré : le serveur n'a rendu aucune question `ce_velo`, l'onglet actif
    // vit donc dans le payload et la liste est vide. On construit tout de suite et on
    // applique l'onglet — seul chemin où le JS écrit dans le DOM au chargement, donc seul
    // chemin où un CLS est possible. Le snippet ne pose l'attribut que dans ce cas.
    if (root.hasAttribute('data-faq-hydrate-now')) {
      hydrate();
      showTab(activeTab);
    }
  }

  // --- Resynchronisation au changement de variante -----------------------------
  // La FAQ est rendue côté serveur avec la variante courante. Le thème change de variante
  // SANS recharger la page (`variant:change`, cf. product-template.liquid:1148) : sans ce
  // listener, les 5 valeurs dépendant de la taille restaient figées sur celle du premier
  // rendu — une réponse pouvait annoncer une fourchette de taille qui n'était plus celle
  // du vélo sélectionné.
  //
  // Écriture DOM déclenchée par une action utilisateur, jamais au chargement : aucun
  // impact sur le CLS. On réutilise la map `#variant-heights-<productId>` déjà émise par
  // product-template.liquid (min/max en mètres) plutôt que d'en produire une seconde.
  function resyncVariantValues(variant) {
    if (!variant) return;
    var root = document.querySelector('[data-faq-root]');
    if (!root) return;

    function setVar(name, text) {
      var nodes = root.querySelectorAll('[data-faq-var="' + name + '"]');
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = text;
    }

    // Prix : même formatage que le reste du thème (product-template.liquid:1153).
    function money(cents) {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat).replace(/,00(?=\s*€)/, '');
      }
      return String(Math.round(cents / 100)) + ' €';
    }

    if (typeof variant.price === 'number') {
      setVar('prix', money(variant.price));
      // Acompte = 10 % plafonné à 100 € — formule de product-form.liquid:99 et du backend.
      setVar('acompte', money(Math.min(Math.round(variant.price * 0.1), 10000)));
    }
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      setVar('prix_neuf', money(variant.compare_at_price));
    } else {
      // Sans `else`, le span gardait le prix neuf de la variante PRÉCÉDENTE : la réponse
      // annonçait une remise qui n'existe pas pour la taille sélectionnée.
      setVar('prix_neuf', '');
    }

    // Délai express : même table par variante que les dates de la fiche (delivery-date-core),
    // dont l'écouteur variant:change, inline, s'exécute avant celui-ci.
    var delivery = window.MintDelivery;
    if (delivery) {
      var days = delivery.expressDays();
      var delayNodes = root.querySelectorAll('[data-faq-var="express_days"]');
      for (var d = 0; d < delayNodes.length; d++) {
        var tpl = delayNodes[d].getAttribute(days === 1 ? 'data-tpl-one' : 'data-tpl-other');
        if (tpl) delayNodes[d].textContent = tpl.replace('#', days);
      }
    }

    // Fourchette de taille : lue dans la map JSON du thème (valeurs en mètres).
    var pid = window.productData && window.productData.id;
    var dataEl = pid ? document.getElementById('variant-heights-' + pid) : null;
    if (!dataEl) return;
    var map;
    try {
      map = JSON.parse(dataEl.textContent);
    } catch (e) {
      debugWarn('variant-heights illisible — fourchette de taille non resynchronisée.');
      return;
    }
    var entry = map[String(variant.id)];
    // `> 0` et non `!= null` : la map de product-template.liquid teste `!= blank` sur la
    // RÉFÉRENCE au metaobject, pas sur min/max. Un metaobject existant avec `max` vide
    // émet donc `0` (nil | divided_by) et non `null` — un test de nullité laissait alors
    // écrire « 0,00 m » dans la réponse au changement de taille, alors que le rendu
    // serveur, lui, se protège correctement.
    if (!entry || !(entry.min > 0) || !(entry.max > 0)) return;
    // 2 décimales imposées : « 1,80 m », pas « 1,8 m » (cohérent avec le rendu Liquid).
    setVar('taille_min', Number(entry.min).toFixed(2).replace('.', ',') + ' m');
    setVar('taille_max', Number(entry.max).toFixed(2).replace('.', ',') + ' m');
  }

  // Mémorisée pour que `hydrate()` puisse rattraper les nœuds nés après un changement de
  // taille : le payload est figé sur la variante du rendu serveur.
  var lastVariant = null;

  document.addEventListener('variant:change', function (e) {
    lastVariant = (e && e.detail && e.detail.variant) || lastVariant;
    resyncVariantValues(lastVariant);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
